import {
  ButtonSeverity,
  ButtonSize,
  ButtonVariant,
  customButtonColorSchemeSeverity,
  type StandardButtonIconParams,
  type StandardButtonProps,
} from '@/types/StandardButtonTypes';

/** PrimeVue props that are styled via scoped CSS custom properties. */
export type PrimeVueButtonStyledProps = {
  severity?: ButtonSeverity;
  outlined?: boolean;
  text?: boolean;
  size?: ButtonSize;
  rounded?: boolean;
  fluid?: boolean;
  loading?: boolean;
  disabled?: boolean;
};

export type ResolvedStandardButtonIcons = {
  leftIcon: string | null;
  rightIcon: string | null;
  icon: string | null;
  isIconOnly: boolean;
};

/** Prefix for a button token CSS variable (without `var()`). */
export function getButtonColorTokenCssVarPrefix(
  severity: ButtonSeverity,
  variant: ButtonVariant = ButtonVariant.SOLID,
): string {
  if (customButtonColorSchemeSeverity.has(severity)) {
    return variant === ButtonVariant.SOLID
      ? `--p-component-color-schemes-button-${severity}`
      : `--p-component-color-schemes-button-${variant}-${severity}`;
  }

  return variant === ButtonVariant.SOLID ? `--p-button-${severity}` : `--p-button-${variant}-${severity}`;
}

/**
 * CSS class StandardButton applies for ComponentColorSchemes.button tokens.
 * Example: `standard-button--text-sentence-title`
 */
export function getButtonColorSchemeClass(
  severity: ButtonSeverity,
  variant: ButtonVariant = ButtonVariant.SOLID,
): string | null {
  if (!customButtonColorSchemeSeverity.has(severity)) {
    return null;
  }

  return `standard-button--${variant}-${severity}`;
}

/** Maps StandardButton props to PrimeVue styled-mode props. */
export function mapStandardButtonPropsToPVStyle(params: StandardButtonProps): PrimeVueButtonStyledProps {
  const variant = params.variant ?? ButtonVariant.SOLID;
  const mapped: PrimeVueButtonStyledProps = {};

  if (
    params.severity != null &&
    params.severity !== ButtonSeverity.PRIMARY &&
    !customButtonColorSchemeSeverity.has(params.severity)
  ) {
    mapped.severity = params.severity;
  }

  if (variant === ButtonVariant.OUTLINED) {
    mapped.outlined = true;
  }

  if (variant === ButtonVariant.TEXT) {
    mapped.text = true;
  }

  // If size is SMALL or LARGE, map it to the PrimeVue size prop
  // If size is NORMAL, it map to the button root tokens
  if (params.size === ButtonSize.SMALL || params.size === ButtonSize.LARGE) {
    mapped.size = params.size;
  }

  if (params.rounded) {
    mapped.rounded = true;
  }

  if (params.fluid) {
    mapped.fluid = true;
  }

  if (params.loading) {
    mapped.loading = true;
  }

  if (params.disabled) {
    mapped.disabled = true;
  }

  return mapped;
}

/** Spinner stroke color — always the button label/foreground token so it contrasts with the fill. */
export function getButtonSpinnerBackground(
  severity: ButtonSeverity = ButtonSeverity.PRIMARY,
  variant: ButtonVariant = ButtonVariant.SOLID,
): string {
  return `var(${getButtonColorTokenCssVarPrefix(severity, variant)}-color)`;
}

/** Fallback for icon-only buttons when no `label` is provided. Keeps kebab-case (e.g. `content-save`). */
export function getIconAccessibilityLabel(icon: string): string {
  return icon.includes(':') ? (icon.split(':').at(-1) ?? icon) : icon;
}

/** when `icon` is passed, it means icon-only layout. IconOnly button can still have different `variant` (`solid` / `outlined` / `text`). */
export function resolveStandardButtonIconOptions(params: StandardButtonIconParams): ResolvedStandardButtonIcons {
  const isIconOnly = params.icon != null;

  if (isIconOnly) {
    return {
      leftIcon: null,
      rightIcon: null,
      icon: params.icon ?? null,
      isIconOnly: true,
    };
  }

  return {
    leftIcon: params.leftIcon ?? null,
    rightIcon: params.rightIcon ?? null,
    icon: null,
    isIconOnly: false,
  };
}
